import patchStateReducer from '@coxy/utils/src/redux/patch-state'
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { actionUserLogout } from '@shared-core/features/auth/actions/global'
import { Themes } from '@shared-core/features/config/models/types'
import type { Locale } from '@shared-core/features/settings/configs/localizations'

export const storeKey = '@redux/settings'

export type SettingsState = {
  locale: string
  localeISO: string
  language: Locale
  // Пользовательские тумблеры
  isHideBalance: boolean
  isVibrationEnabled: boolean
  developerMode: boolean
  theme: Themes
  phone: string
  // Dev-инструменты API: мок-бэк, задержка и процент ошибок (переживают логаут)
  apiDelayMs: number
  apiErrorRate: number
  apiBaseUrlOverride: string
  apiMockBaseUrl: string
}

const initialState: SettingsState = {
  locale: '',
  localeISO: '',
  language: 'ru',
  isHideBalance: false,
  isVibrationEnabled: true,
  developerMode: false,
  theme: Themes.system,
  phone: '',
  apiDelayMs: 0,
  apiErrorRate: 0,
  apiBaseUrlOverride: '',
  apiMockBaseUrl: '',
}

export const whitelist = [
  'locale',
  'localeISO',
  'language',
  'isHideBalance',
  'isVibrationEnabled',
  'developerMode',
  'theme',
  'phone',
  'apiDelayMs',
  'apiErrorRate',
  'apiBaseUrlOverride',
  'apiMockBaseUrl',
]

export const settingsSlice = createSlice({
  name: storeKey,
  initialState,
  reducers: {
    patchSettingsState: (state, action: PayloadAction<Partial<SettingsState>>) => {
      patchStateReducer(state, action)
    },
    toggleDeveloperMode: (state: SettingsState, action: PayloadAction<boolean>) => {
      state.developerMode = action.payload
    },
    toggleHideBalance: (state: SettingsState) => {
      state.isHideBalance = !state.isHideBalance
    },
    setHideBalanceStatus: (state: SettingsState, action: PayloadAction<boolean>) => {
      state.isHideBalance = action.payload
    },
    toggleVibration: (state: SettingsState) => {
      state.isVibrationEnabled = !state.isVibrationEnabled
    },
    setLanguage: (state: SettingsState, action: PayloadAction<Locale>) => {
      state.language = action.payload
    },
    setLocale: (state: SettingsState, action: PayloadAction<{ locale: string; localeISO: string }>) => {
      state.locale = action.payload.locale
      state.localeISO = action.payload.localeISO
    },
    setUiTheme: (state: SettingsState, action: PayloadAction<Themes>) => {
      state.theme = action.payload
    },
    setUserPhone: (state: SettingsState, action: PayloadAction<string>) => {
      state.phone = action.payload
    },
    toggleUiTheme: (state: SettingsState) => {
      state.theme = state.theme === Themes.light ? Themes.dark : Themes.light
    },
    setApiDelayMs: (state: SettingsState, action: PayloadAction<number>) => {
      state.apiDelayMs = action.payload
    },
    setApiErrorRate: (state: SettingsState, action: PayloadAction<number>) => {
      state.apiErrorRate = action.payload
    },
    setApiBaseUrlOverride: (state: SettingsState, action: PayloadAction<string>) => {
      state.apiBaseUrlOverride = action.payload
    },
    setApiMockBaseUrl: (state: SettingsState, action: PayloadAction<string>) => {
      state.apiMockBaseUrl = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actionUserLogout, (state: SettingsState) => {
      // При логауте дев-настройки API и выбор языка/темы сохраняем — остальное сбрасывается.
      Object.assign(state, initialState, {
        language: state.language,
        isHideBalance: state.isHideBalance,
        isVibrationEnabled: state.isVibrationEnabled,
        theme: state.theme,
        phone: state.phone,
        apiDelayMs: state.apiDelayMs,
        apiErrorRate: state.apiErrorRate,
        apiBaseUrlOverride: state.apiBaseUrlOverride,
        apiMockBaseUrl: state.apiMockBaseUrl,
      })
    })
  },
})

export const {
  patchSettingsState,
  setLanguage,
  setLocale,
  setHideBalanceStatus,
  toggleHideBalance,
  toggleDeveloperMode,
  toggleVibration,
  setUiTheme,
  setUserPhone,
  toggleUiTheme,
  setApiDelayMs,
  setApiErrorRate,
  setApiBaseUrlOverride,
  setApiMockBaseUrl,
} = settingsSlice.actions
